import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { fetchSettings } from "../../redux/userSetting";
import { fetchledger } from "../../redux/ledger";
import { LedgerBranchOpeningMaster } from "../../core/json/OpeningMaster";
import OpeningBalanceModal from "../../components/accounting/OpeningBalanceModal";
import {
  getOpeningBalance,
  deleteOpeningBalance,
} from "../../services/AccountApiServices";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, money, num } from "../../components/report/reportFormat";

const OpeningBalance = () => {
  const [journalEntry, setJournalEntry] = useState([]);
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  const { formatedLedger } = useSelector((state) => state.ledger);
  const MySwal = withReactContent(Swal);

  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Journal")
    );
    dispatch(fetchledger());
  }, []);
  useEffect(() => {
    dispatch(fetchledger());
    dispatch(fetchSettings(branch));
    handleOpeningbal();
    // handleFilter();
  }, [branch]);

  const handleOpeningbal = async () => {
    try {
      const response = await getOpeningBalance(branch);
      const invoice = Array.isArray(response) ? response : [];
      const filterInvoiceByBranch = invoice.filter(
        (invoice) => invoice?.branch?.guid === branch?.guid
      );
      setJournalEntry(filterInvoiceByBranch);
    } catch (error) {
      console.error(
        "Failed to fetch journal entry",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  const handleEditClick = async (guid) => {
    try {
      const response = journalEntry?.find((sales) => sales.guid === guid);
      setUpdateData(response);

      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching sales invoice by GUID",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  const handleDeleteClick = (obj) => {
    // deleted through its own endpoint, not Journal/Delete (AC-13)
    const objpayment = new LedgerBranchOpeningMaster();
    objpayment.branch = branch;
    objpayment.guid = obj.guid;
    objpayment.id = obj.id;
    objpayment.updatedUser = userId?.id;
    objpayment.createdUser = userId?.id;
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteOpeningBalance(objpayment);
          if (response.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The opening balance has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handleOpeningbal();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error("Error deleting sales quotation:", error);
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  /* ---------------- list ---------------- */
  const ledgerIdOf = (l) => l?.ledger?.guid ?? l?.ledgerID;
  const ledgerName = (l) =>
    l?.ledger?.name ||
    formatedLedger?.find((e) => e.value == ledgerIdOf(l))?.label ||
    "";

  // one flat record per opening balance entry; the keys match the columns
  const rows = useMemo(
    () =>
      journalEntry.map((r) => {
        const lines = r.ledgerBranchOpeningDetails || [];
        const debit = lines.reduce((a, l) => a + num(l?.debit), 0);
        const credit = lines.reduce((a, l) => a + num(l?.credit), 0);
        return {
          guid: r.guid,
          raw: r,
          date: dateOf(r.date),
          billNo: r.no != null ? String(r.no) : "",
          remarks: r.remarks || "",
          items: lines.length,
          debit,
          credit,
          net: debit,
          lines,
        };
      }),
    [journalEntry]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      { k: "billNo", t: "Entry#", type: "doc" },
      { k: "remarks", t: "Remarks" },
      { k: "items", t: "Lines", type: "int", off: true },
      { k: "net", t: "Debit", type: "money" },
      { k: "credit", t: "Credit", type: "money" },
    ],
    []
  );

  const tabs = useMemo(() => [{ id: "all", label: "Opening Balance" }], []);

  const filters = useMemo(
    () => [
      {
        key: "ledger",
        label: "Ledger",
        items: (formatedLedger || []).map((l) => ({ v: l.value, t: l.label || "—" })),
        match: (r, v) => r.lines.some((l) => v.includes(l?.ledger?.guid ?? l?.ledgerID)),
      },
    ],
    [formatedLedger]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.remarks) ||
      hit(r.date) ||
      String(r.net).includes(q) ||
      String(r.credit).includes(q) ||
      r.lines.some(
        (l) =>
          hit(ledgerName(l)) ||
          hit(l?.refNo) ||
          hit(l?.remarks) ||
          String(l?.debit ?? "").includes(q) ||
          String(l?.credit ?? "").includes(q)
      )
    );
  };

  const drawerContent = (r) => ({
    title: r.remarks || `Opening balance ${r.billNo}`,
    subtitle: [`Entry ${r.billNo}`, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: r.remarks || r.billNo,
    meta: [
      { k: "Remarks", v: r.remarks },
      { k: "Lines", v: intg(r.items) },
      { k: "Total debit", v: money(r.debit) },
      { k: "Total credit", v: money(r.credit) },
    ],
    lines: {
      title: "Opening balance lines",
      columns: [
        { t: "Ledger" },
        { t: "Ref No." },
        { t: "Debit", align: "right" },
        { t: "Credit", align: "right" },
      ],
      rows: r.lines.map((l) => [
        <div key="nm" className="axr-nm">{ledgerName(l) || "Unknown ledger"}</div>,
        l?.refNo || "—",
        money(l?.debit),
        money(l?.credit),
      ]),
    },
    net: { l: "Total debit", v: money(r.net) },
  });

  return (
    <TransactionScreen
      title="Opening Balance"
      subtitle="Manage opening balance here"
      unit="entries"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, remarks, ledger, ref no, amount…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "net:-1", t: "Largest debit" },
        { v: "net:1", t: "Smallest debit" },
      ]}
      exportName="Opening Balance"
      onRefresh={handleOpeningbal}
      canAdd={true /* the old page had its permit?.add check commented out */}
      newLabel="New opening balance"
      onNew={() => {
        setModalMode("add"); // Set the modal mode to 'add'
        setModalOpen(true); // Open the modal
      }}
      canEdit={permit?.edit}
      onEdit={(r) => handleEditClick(r.guid)}
      editLabel="Edit opening balance"
      canDelete={permit?.deletion}
      onDelete={(r) => handleDeleteClick(r.raw)}
      drawerContent={drawerContent}
      keysDisabled={modalOpen}
    >
      {/* Reason Modal */}
      {modalOpen && (
        <OpeningBalanceModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handleOpeningbal();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default OpeningBalance;
